<template>
  <section class="plans-table">
    <h2>
      {{ data?.titles?.title }}
    </h2>
    <div class="plans-table-content">

      <table>
        <thead>
          <tr>
            <th scope="col"></th>
            <template
              v-for="plan in data?.feature_tiles"
              v-bind:key="plan.documentId"
            >
              <th scope="col">
                {{ plan?.title }}
              </th>
              <th class="space" />
            </template>
          </tr>
        </thead>
        <tbody
          v-for="(item, index) in augmentedCategories"
          v-bind:key="index"
        >
          <tr>
            <td data-label="">
              {{ item?.label }}
              <Tooltip v-if="item?.description">
                <template v-slot:visible>
                  <span class="question-mark">?</span>
                </template>
                <template v-slot:tip>
                  {{ item?.description }}
                </template>
              </Tooltip>
            </td>
            <template
              v-for="plan in data?.feature_tiles"
              v-bind:key="plan.id"
            >
              <td :data-label="plan?.title" />
              <td class="space" />
            </template>
          </tr>

          <tr
            v-for="(feature, indexFeat) in item?.features"
            v-bind:key="indexFeat"
            :class="{ 'plans-table__row--highlight': feature?.highlight }"
          >
            <td data-label="">
              {{ feature?.label }}
              <Tooltip v-if="feature?.description">
                <template v-slot:visible>
                  <span class="question-mark">?</span>
                </template>
                <template v-slot:tip>
                  {{ feature?.description }}
                </template>
              </Tooltip>
            </td>
            <template
              v-for="(plan, planIndex) in data?.feature_tiles"
              v-bind:key="planIndex"
            >
              <td :data-label="plan?.title">
                <IconCheck v-if="feature?.advanced?.[planIndex] === 'true'" />
                <template v-else-if="feature?.advanced?.[planIndex]">
                  {{ feature?.advanced?.[planIndex] }}
                </template>
              </td>
              <td class="space" />
            </template>
          </tr>

          <tr>
            <td data-label=""></td>
            <template
              v-for="plan in data?.feature_tiles"
              v-bind:key="plan.documentId"
            >
              <td :data-label="plan?.title" />
              <td class="space" />
            </template>
          </tr>
        </tbody>
      </table>

    </div>
  </section>
</template>

<script>
import { mapState } from 'pinia'
import { useStore } from '@/stores/index'
import IconCheck from '@/components/Icons/Check.vue'
import Tooltip from '@/components/Text/Tooltip.vue'

/* Match the legacy "auto-check / veille" feature labels coming from
   Strapi so we can filter them out client-side. We're replacing them
   with the hardcoded "Heartbeat / Battement" row below. Permissive
   substring match (case-insensitive) so casing/wording variants
   ("auto-check", "Enhanced auto-check", "veille renforcée", etc.)
   are all caught without needing to enumerate every spelling.

   TODO: once the Strapi auto-check rows are deleted or renamed, this
   matcher can go away, it's a temporary client-side filter to avoid
   a CMS round-trip. */
const isAutoCheckLabel = (label) => {
  if (!label) return false
  const normalized = String(label).trim().toLowerCase()
  return (
    normalized.includes('auto-check') ||
    normalized.includes('auto check') ||
    normalized.includes('autocheck') ||
    normalized.includes('veille')
  )
}

/* Match the legacy generic "Support" feature row from Strapi so we
   can filter it out, it's being replaced by the two specific
   hardcoded rows below (Support by email + Priority support).
   Strict equality (not substring) so the specific rows we add
   ("Support by email", "Support par email", etc.) aren't caught. */
const isLegacySupportLabel = (label) => {
  if (!label) return false
  const normalized = String(label).trim().toLowerCase()
  return (
    normalized === 'support' ||
    normalized === 'customer support' ||
    normalized === 'support client'
  )
}

/* Match the "Support & auto-check" CATEGORY heading from Strapi so
   we can rename it to "Other / Autres" client-side. Catches a few
   wording variants (& vs and, presence of "auto-check" or "veille").
   Same temporary-filter rationale as above, delete once Strapi is
   renamed. */
const isSupportAutoCheckCategory = (label) => {
  if (!label) return false
  const normalized = String(label).trim().toLowerCase()
  if (!normalized.includes('support')) return false
  return (
    normalized.includes('auto-check') ||
    normalized.includes('auto check') ||
    normalized.includes('autocheck') ||
    normalized.includes('veille')
  )
}

/* Localized label to swap in for the renamed category. */
const OTHER_CATEGORY_LABEL = {
  'en-US': 'Other',
  'fr-FR': 'Autres',
}

/* The legacy "Visibility / Visibilité" row in Strapi describes WHO
   can receive a wish. We're renaming it to "Recipient targets" to
   match how the concept reads inside the app (and to make the column
   values read as targets, not as a yes/no toggle). Override applies
   the new label + new values regardless of what Strapi has. */
const isVisibilityFeature = (label) => {
  if (!label) return false
  const normalized = String(label).trim().toLowerCase()
  return (
    normalized === 'visibility' ||
    normalized === 'visibilité' ||
    normalized === 'visibilite'
  )
}

const RECIPIENT_TARGETS_OVERRIDE = {
  'en-US': {
    label: 'Recipient targets',
    /* No tooltip, the values in the row ("Circle", "Circle or
       Worldwide") are self-explanatory in context, and a separate
       Features-page section will carry the longer explanation. */
    description: '',
    advanced: ['', 'Circle', 'Circle or Worldwide'],
  },
  'fr-FR': {
    label: 'Destinataires',
    description: '',
    advanced: ['', 'Cercle', 'Cercle ou Monde'],
  },
}

/* Hardcoded rows added to the "Other" category (renamed in-code from
   "Support & auto-check"). All three live here instead of Strapi for
   the same reason: brand positioning is still settling and we want
   to iterate copy in code. Promote to CMS once locked.

   `advanced` mirrors the CMS shape: index 0 = first column (Free),
   1 = Super, 2 = Master. Special values:
     - 'true'  → renders the check icon
     - ''      → renders nothing (the "none" column behavior)
     - any other string → rendered as-is (used for the recipient-targets
                          override below, e.g. "Circle or Worldwide") */
const HEARTBEAT_ROW = {
  'en-US': {
    label: 'Heartbeat',
    description: 'A weekly nudge to confirm you\'re still around. Tap once and your wishes stay sealed.',
    advanced: ['', 'true', 'true'],
    /* Flag for the template to apply the row-level highlight styling
       (pink wash + bolder label + ♥ accent). The Heartbeat row is
       the headline paid differentiator, the only one we want the
       eye to land on at a glance. Other rows leave this falsy. */
    highlight: true,
  },
  'fr-FR': {
    label: 'Battement',
    description: 'Un petit « toujours là ? » chaque semaine. Une tape et vos volontés restent scellées.',
    advanced: ['', 'true', 'true'],
    highlight: true,
  },
}

const SUPPORT_BY_EMAIL_ROW = {
  'en-US': {
    label: 'Support by email',
    description: '',
    /* Free tier intentionally left without email support, paid
       differentiator. Pre-launch this was `'true'` (✓ on all
       three) but we dialed it back to make the Super/Master tiers
       feel more concretely valuable. */
    advanced: ['', 'true', 'true'],
  },
  'fr-FR': {
    label: 'Support par email',
    description: '',
    advanced: ['', 'true', 'true'],
  },
}

/* No tooltip on Priority support, the label is self-explanatory
   and the row sits next to "Support by email" so the contrast
   (everyone gets email vs only Master gets priority) is obvious
   from the column ticks alone. */
const PRIORITY_SUPPORT_ROW = {
  'en-US': {
    label: 'Priority support',
    description: '',
    advanced: ['', '', 'true'],
  },
  'fr-FR': {
    label: 'Support prioritaire',
    description: '',
    advanced: ['', '', 'true'],
  },
}

export default {
  name: 'PlansTableSection',
  components: {
    IconCheck,
    Tooltip
  },
  props: {
    data: Object
  },
  computed: {
    ...mapState(useStore, ['language']),
    heartbeatRow() {
      return HEARTBEAT_ROW[this.language] || HEARTBEAT_ROW['en-US']
    },
    supportByEmailRow() {
      return SUPPORT_BY_EMAIL_ROW[this.language] || SUPPORT_BY_EMAIL_ROW['en-US']
    },
    prioritySupportRow() {
      return PRIORITY_SUPPORT_ROW[this.language] || PRIORITY_SUPPORT_ROW['en-US']
    },
    otherCategoryLabel() {
      return OTHER_CATEGORY_LABEL[this.language] || OTHER_CATEGORY_LABEL['en-US']
    },
    recipientTargetsOverride() {
      return RECIPIENT_TARGETS_OVERRIDE[this.language] || RECIPIENT_TARGETS_OVERRIDE['en-US']
    },
    /* Three transformations in one pass:
        1. Drop any "auto-check / veille" rows (legacy Strapi entries
           being phased out in favor of the hardcoded Battement row).
        2. Wherever the "Visibility / Visibilité" row appears, replace
           its label + tooltip + values with the new "Recipient
           targets" override (Free: none, Super: Circle, Master:
           Circle or Worldwide).
        3. In the "Support & auto-check" category specifically:
           - rename the heading to "Other / Autres"
           - PREPEND the three hardcoded rows (Heartbeat, then
             Support by email, then Priority support) so they sit
             above any leftover Strapi rows. Order matters here:
             Heartbeat is the differentiator and should lead.
       Target category is found by label match
       (isSupportAutoCheckCategory), with a fall back to the last
       category if Strapi labels drift, so the new rows never
       disappear silently.
       We mutate features in place rather than building a standalone
       <tbody> to inherit the CMS styling (mobile responsive headers,
       hover wash, row dividers) for free. */
    augmentedCategories() {
      const cats = this.data?.feature_categories || []
      if (cats.length === 0) return cats

      let targetIdx = cats.findIndex((c) => isSupportAutoCheckCategory(c?.label))
      if (targetIdx === -1) targetIdx = cats.length - 1

      return cats.map((cat, i) => {
        // 1 & 2: drop auto-check + legacy "Support" rows, override
        //        Visibility row.
        const transformed = (cat?.features || [])
          .filter((f) => !isAutoCheckLabel(f?.label) && !isLegacySupportLabel(f?.label))
          .map((f) => {
            if (isVisibilityFeature(f?.label)) {
              return { ...f, ...this.recipientTargetsOverride }
            }
            return f
          })

        if (i !== targetIdx) {
          return { ...cat, features: transformed }
        }

        // 3: target category, rename + prepend the three hardcoded rows.
        return {
          ...cat,
          label: this.otherCategoryLabel,
          features: [
            this.heartbeatRow,
            this.supportByEmailRow,
            this.prioritySupportRow,
            ...transformed,
          ],
        }
      })
    },
  },
}
</script>

<style
  lang="scss"
  scoped
>
.plans-table {
  padding: 24px 0 56px;
}

.plans-table>h2 {
  text-align: center;
  margin: 0 0 28px;
}

.plans-table-content {
  text-align: left;

  table {
    width: 100%;
    table-layout: fixed;
    border-spacing: 16px 0px;
    border-collapse: collapse;

    /* Column header row. clean, no heavy background. The popular
       middle column (Super) gets a soft pink tint so the eye lands
       there. Master sits in normal grey. Free is the leftmost,
       neutral. */
    th {
      padding: 16px 12px 24px;
      text-align: center;
      font-weight: 600;
      font-size: 15px;
      letter-spacing: 0.3px;
      border-bottom: 2px solid rgba(0, 0, 0, 0.08);
    }

    /* Column tint via nth-child. We have a "space" td after every
       real column, so real columns are 1,3,5,7. Index 3 = Super. */
    th:nth-child(3),
    td:nth-child(3) {
      background-color: var(--bzioup-color-pink-soft);
    }

    tr {
      padding: 6px;
    }

    td {
      padding: 12px 10px;
      text-align: center;
      font-size: 14px;
      color: var(--bzioup-color-Y);
    }

    .space {
      width: 6px;
      padding: 0;
      background: transparent !important;
    }

    /* The first row of each tbody is the category header (label
       only). Treat it as a section heading: pink left accent,
       slightly larger text, no background. */
    tbody>tr:first-child>td:first-child {
      font-weight: 600;
      font-size: 15px;
      padding: 20px 12px 8px;
      border-left: 3px solid var(--bzioup-color-pink);
      padding-left: 14px;
      background: transparent;
      color: var(--bzioup-color-Z);
    }

    tbody>tr:first-child>td:not(:first-child) {
      padding-top: 20px;
    }

    /* Feature label column (first td of each non-header row). */
    tbody>tr>td:first-child {
      text-align: left;
      background-color: transparent;
      color: var(--bzioup-color-Y);
    }

    /* Hover lift on data rows. soft pink wash. The popular column's
       base tint persists, just darkens slightly via the row hover
       so the visual hierarchy holds. */
    tr:not(:first-child):not(:last-child):hover td {
      background-color: rgba(218, 138, 139, 0.06);
    }

    /* Subtle row divider on every feature row except the last in a
       category. cleaner than a uniform background. */
    tbody>tr:not(:first-child):not(:last-child) td {
      border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    }

    /* Check icon in brand pink. */
    svg {
      vertical-align: middle;
      color: var(--bzioup-color-pink);
    }

    /* Highlighted row, used for the hardcoded Heartbeat differentiator.
       Pink wash across all cells, bolder label, slight border emphasis
       so the eye lands here when scanning the table. Nested INSIDE the
       table scope to win specificity against:
         - td:nth-child(3) column tint (Super column)
         - tr:not(...):hover td hover wash
       Source order also matters, placing this rule below them is
       enough on its own when specificities tie. */
    tr.plans-table__row--highlight > td {
      background-color: rgba(218, 138, 139, 0.14);
    }

    tr.plans-table__row--highlight:hover > td {
      background-color: rgba(218, 138, 139, 0.20);
    }

    /* Label cell: darker text + heavier weight so the row reads as
       "the important one" at a glance. Has to override the base
       `tbody>tr>td:first-child` rule which sets `color: var(--Y)`
       (the dim body text color). */
    tbody > tr.plans-table__row--highlight > td:first-child {
      color: var(--bzioup-color-Z);
      font-weight: 600;
    }
  }
}


@media screen and (max-width: $mq-medium) {
  .plans-table-content table {

    thead {
      display: none;
    }

    tbody {
      margin-top: 20px;
    }

    .space {
      display: none;
    }

    tr {
      padding: 0px;
      display: block;
      margin-bottom: 10px;
      border-bottom: 3px solid var(--bzioup-bg-color-highlight-opacity);
    }

    tbody>tr:first-child {
      border: 0;
      text-transform: uppercase;
      background-color: transparent;

      >td:first-child {
        border: none;
      }

      >td:not(:first-child) {
        display: none;
      }
    }

    tbody>tr:last-child {
      display: none;
    }

    td::before {
      float: left;
      font-weight: bold;
      content: attr(data-label);
    }

    td {
      display: block;
      text-align: right;
      border-bottom: 1px solid var(--bzioup-bg-color-highlight-opacity);
    }

    tr:not(:first-child):not(:last-child) td {
      height: 20px;
      padding-left: 8px;
    }

    tr:hover:not(:first-child):not(:last-child) td {
      background-color: var(--bzioup-bg-color-B);
    }

    tbody>tr>td:first-child {
      text-align: right;
      background-color: var(--bzioup-bg-color-B);
    }

    tbody>tr:first-child>td:first-child {
      text-align: right;
      background-color: transparent;
    }

    td:last-child {
      border-bottom: 0;
    }
  }
}
</style>
